import { batch, computed, effect, signal } from '@preact/signals'
import * as engine from '../core/engine'
import { DEFAULT_DAY, clampDaySettings, isNightWake, nextDayStart, type DaySettings } from '../core/day'
import { planReminders, type Reminder } from '../core/reminders'
import { t } from '../i18n'
import { weightPoints } from '../core/weight'
import { forProfile } from '../core/programs'
import type { ChildProfile, Phase, WakeState } from '../core/types'
import { addEvent, loadEvents, markEventDeleted, type DayEvent, type EndReason } from '../stores/events'
import { scheduleSync } from '../api/sync'
import type { Platform } from '../platform/platform'
import type { Storage } from '../platform/storage'
import { addNote as addNoteToStore, loadNotes, removeNote as removeNoteFromStore, updateNote as updateNoteInStore, type Note } from '../stores/notes'
import { loadWindows, logWindow } from '../stores/history'
import { applyCustomPhases, clearCustomPhases, isCustom as isCustomPhases, saveCustomPhases } from '../stores/phases'
import { loadProfile, saveProfile as saveProfileToStore } from '../stores/profile'
import { loadWindowStart, saveWindowStart } from '../stores/window'
import { KEY } from '../stores/keys'
import { tellServerTourSeen } from '../api/account'
import { defaultProgram } from '../core/programs'
import { rememberKid } from '../stores/kids-boot'

/**
 * Состояние приложения. Хранилище подключается при старте, дальше все действия
 * пишут в него и обновляют сигналы; отдельного «тикающего» таймера нет —
 * время живёт в сигнале [now], а фаза и отсчёт вычисляются от него.
 */

export type Tab = 'timer' | 'myDay' | 'calendar' | 'menu'

/** Вкладка календаря: лента месяцев или аналитика веса. Профиль умеет открыть вес напрямую. */
export const calendarTab = signal<'month' | 'weight'>('month')
/** Раздел аналитики: окна бодрствования или вес. Бодрствование первым — это то, ради чего приложение. */
export const analyticsKind = signal<'wake' | 'weight'>('wake')
/** День, лист которого календарь откроет при показе (сцена холста «лист по дате»). */
export const calendarDayToOpen = signal<number | null>(null)

export const now = signal(Date.now())
export const tab = signal<Tab>('timer')
export const profile = signal<ChildProfile | null>(null)
export const windowStart = signal<number | null>(null)
export const customPhases = signal<Phase[] | null>(null)
export const notes = signal<Note[]>([])
export const windows = signal<number[]>([])
/** Журнал: фазы, концы окон, ночные пробуждения, взвешивания. */
export const events = signal<DayEvent[]>([])
/** Режим дня: границы дня и напоминание о подъёме (настройки «Режим дня»). */
export const daySettings = signal<DaySettings>(DEFAULT_DAY)
/** Малыш спит с этого момента — ответ «да, уснул». От него считаются напоминания о подъёме. */
export const sleepingSince = signal<number | null>(null)
export const ready = signal(false)

let storage: Storage | null = null
let platform: Platform | null = null

/** Рекомендованная программа по возрасту; пересчитывается раз в час вместе с [now]. */
export const baseProgram = computed(() => {
  const p = profile.value
  return p === null ? defaultProgram() : forProfile(p, now.value)
})

/** Программа с учётом правок «Моего дня». */
export const program = computed(() => {
  const phases = customPhases.value
  return phases === null ? baseProgram.value : { ...baseProgram.value, phases }
})

export const state = computed<WakeState>(() => ({ program: program.value, windowStartMillis: windowStart.value }))

export const phaseIndex = computed(() => engine.currentIndex(state.value, now.value))
export const isRunning = computed(() => windowStart.value !== null)
/** Сколько секунд последняя фаза идёт сверх плана. По времени окно не закрывается. */
export const overtime = computed(() => engine.overtimeSeconds(state.value, now.value))
/**
 * Чем и когда кончилось последнее окно в этом запуске приложения: после «да, уснул»
 * экран «Готовы?» желает доброго сна, после сброса без ответа — говорит, во сколько окно
 * закрылось. Не хранится: при следующем открытии это уже прошлое.
 */
export const justClosed = signal<{ reason: EndReason; at: number } | null>(null)
/** Ночное пробуждение идёт: момент нажатия «Проснулся» ночью. Без фаз и уведомлений. */
export const nightWake = signal<number | null>(null)
/** Сейчас ночь по правилам дня: «Проснулся» запишется ночным пробуждением, а не окном. */
export const isNightNow = computed(() => isNightWake(now.value, daySettings.value))
export const currentPhase = computed(() => program.value.phases[phaseIndex.value] ?? null)
export const isCustomProgram = signal(false)

/**
 * Показывали ли человеку тур «Как это работает».
 *
 * Раньше тур решался на лету в конце анкеты приложения: «профиля не было — значит,
 * человек новый». С сайта это не работало — там регистрация идёт на лендинге, и в
 * приложение человек приходил уже с профилем, минуя тур. Теперь это записанный факт:
 * на устройстве и, если есть аккаунт, на сервере — чтобы на втором телефоне тур
 * не начинался заново.
 */
export const tourSeen = signal(false)

/** Тур досмотрели или пропустили. */
export async function markTourSeen(): Promise<void> {
  tourSeen.value = true
  if (storage !== null) await storage.set(KEY.tourSeen, '1')
  await tellServerTourSeen()
}

/** Сервер сказал, что тур человеку уже показывали (вошёл на другом устройстве). */
export async function rememberTourSeen(): Promise<void> {
  if (tourSeen.value) return
  tourSeen.value = true
  if (storage !== null) await storage.set(KEY.tourSeen, '1')
}

/** Подключает хранилище и читает всё, что в нём лежит. */
export async function init(target: Storage | Platform): Promise<void> {
  const isPlatform = (value: Storage | Platform): value is Platform => 'stateChanged' in value
  platform = isPlatform(target) ? target : null
  storage = isPlatform(target) ? target.storage : target
  target = storage
  const loaded = await loadProfile(target)
  const start = await loadWindowStart(target)
  const base = loaded === null ? defaultProgram() : forProfile(loaded, Date.now())
  const phases = (await applyCustomPhases(target, base)).phases
  batch(() => {
    profile.value = loaded
    windowStart.value = start
    customPhases.value = phases === base.phases ? null : phases
    isCustomProgram.value = false
    ready.value = true
  })
  nightWake.value = await загрузитьНочное(target)
  tourSeen.value = (await target.get(KEY.tourSeen)) === '1'
  isCustomProgram.value = await isCustomPhases(target, base.id)
  notes.value = await loadNotes(target)
  windows.value = await loadWindows(target)
  events.value = await loadEvents(target)
  daySettings.value = await загрузитьРежимДня(target)
  sleepingSince.value = await загрузитьЧисло(target, KEY.sleepingSince)
  // Изменение пришло извне: с часов, из другой вкладки или от будильника.
  // Перечитываем экраны и обязательно отдаём это серверу — иначе окно, начатое
  // на часах, осталось бы только на телефоне.
  target.onChange?.(() => {
    void refresh().then(() => scheduleSync())
  })
}

/**
 * Стирает всё, что знает устройство о ребёнке. Нужно там, где данные показывать больше
 * нельзя: аккаунт удалён или человек вышел из него.
 *
 * Ключ устройства для уведомлений не трогаем — это не данные человека, а адрес доставки:
 * тот же телефон сможет подписаться заново, не заводя новую запись на сервере.
 */
export async function wipeLocal(): Promise<void> {
  if (storage === null) return
  for (const key of await storage.keys()) {
    if (key === 'device_token') continue
    await storage.remove(key)
  }
  batch(() => {
    profile.value = null
    windowStart.value = null
    customPhases.value = null
    isCustomProgram.value = false
    notes.value = []
    windows.value = []
    events.value = []
    daySettings.value = DEFAULT_DAY
    sleepingSince.value = null
    justClosed.value = null
    nightWake.value = null
  })
}

/** Перечитывает данные: пригодится, когда их меняет другая вкладка, часы или сервер. */
export async function refresh(): Promise<void> {
  if (storage === null) return
  const loaded = await loadProfile(storage)
  const base = loaded === null ? defaultProgram() : forProfile(loaded, Date.now())
  batch(() => {
    profile.value = loaded
    windowStart.value = null
  })
  windowStart.value = await loadWindowStart(storage)
  nightWake.value = await загрузитьНочное(storage)
  const applied = await applyCustomPhases(storage, base)
  customPhases.value = applied.phases === base.phases ? null : applied.phases
  isCustomProgram.value = await isCustomPhases(storage, base.id)
  notes.value = await loadNotes(storage)
  windows.value = await loadWindows(storage)
  events.value = await loadEvents(storage)
  daySettings.value = await загрузитьРежимДня(storage)
  sleepingSince.value = await загрузитьЧисло(storage, KEY.sleepingSince)
}

export function startTicking(intervalMs = 1000): () => void {
  const id = setInterval(() => {
    now.value = Date.now()
  }, intervalMs)
  const onVisible = () => {
    if (document.visibilityState === 'visible') now.value = Date.now()
  }
  document.addEventListener('visibilitychange', onVisible)
  return () => {
    clearInterval(id)
    document.removeEventListener('visibilitychange', onVisible)
  }
}

async function загрузитьЧисло(target: Storage, key: string): Promise<number | null> {
  const raw = await target.get(key)
  const at = raw === null ? Number.NaN : Number(raw)
  return Number.isFinite(at) && at > 0 ? at : null
}

const загрузитьНочное = (target: Storage) => загрузитьЧисло(target, KEY.nightWakeStart)

async function загрузитьРежимДня(target: Storage): Promise<DaySettings> {
  return clampDaySettings({
    dayStartMin: (await загрузитьЧисло(target, KEY.dayStartMin)) ?? DEFAULT_DAY.dayStartMin,
    bedtimeMin: (await загрузитьЧисло(target, KEY.bedtimeMin)) ?? DEFAULT_DAY.bedtimeMin,
    wakePush: (await target.get(KEY.wakePush)) !== '0',
  })
}

/** Настройки «Режима дня»: сохранить и пересчитать напоминания. */
export async function saveDaySettings(next: DaySettings): Promise<void> {
  const clamped = clampDaySettings(next)
  daySettings.value = clamped
  if (storage === null) return
  await storage.set(KEY.dayStartMin, String(clamped.dayStartMin))
  await storage.set(KEY.bedtimeMin, String(clamped.bedtimeMin))
  await storage.set(KEY.wakePush, clamped.wakePush ? '1' : '0')
  // Часы получают границы дня вместе с профилем, а профиль едет по своей метке времени
  await storage.set(KEY.profileUpdatedAt, String(Date.now()))
  await announceState()
}

/** Запись в журнал — и сигнал следом, чтобы календарь увидел её сразу. */
async function запись(target: Storage, event: DayEvent): Promise<void> {
  await addEvent(target, event)
  events.value = await loadEvents(target)
}

/** Спит с этого момента или не спит: хранится, чтобы напоминания пережили перезапуск. */
async function записатьСон(at: number | null): Promise<void> {
  sleepingSince.value = at
  if (storage === null) return
  if (at === null) await storage.remove(KEY.sleepingSince)
  else await storage.set(KEY.sleepingSince, String(at))
}

/** Напоминания из текущего состояния: их считает приложение, сервер лишь доставляет. */
function напоминания(): Reminder[] {
  const child = profile.value
  if (child === null) return []
  return planReminders({
    profile: child,
    program: program.value,
    settings: daySettings.value,
    now: Date.now(),
    asleepAt: sleepingSince.value,
    weights: weightPoints(events.value, child.birthMillis, child.birthWeightGrams),
  })
}

/** Убрать взвешивание: ошибку на весах можно исправить. Запись остаётся меткой ради обмена. */
export async function removeWeight(event: DayEvent): Promise<void> {
  if (storage === null || event.kind !== 'weight') return
  await markEventDeleted(storage, event)
  events.value = await loadEvents(storage)
  scheduleSync()
  await announceState()
}

/**
 * Окно бодрствования, вписанное руками в календаре (просьба 12.10.2026).
 *
 * Одна запись на всё окно: `window` — начало, `at` — конец, причина «уснул» — чтобы оно
 * шло в аналитику наравне с отсчитанными таймером.
 */
export async function addManualWindow(start: number, end: number): Promise<void> {
  if (storage === null || end <= start) return
  await запись(storage, { id: end, kind: 'window_end', at: end, window: start, reason: 'asleep', manual: true })
  scheduleSync()
  await announceState()
}

/** Убрать окно, вписанное руками: ошибку во времени исправляют. */
export async function removeManualWindow(event: DayEvent): Promise<void> {
  if (storage === null || event.kind !== 'window_end' || event.manual !== true) return
  await markEventDeleted(storage, event)
  events.value = await loadEvents(storage)
  scheduleSync()
  await announceState()
}

/** Взвешивание: запись в журнал, обмен и пересчёт ориентира по весу. */
export async function addWeight(at: number, grams: number): Promise<void> {
  if (storage === null) return
  await запись(storage, { id: at, kind: 'weight', at, window: at, grams })
  scheduleSync()
  await announceState()
}

// ---- действия ----

async function persistWindow(next: number | null): Promise<void> {
  windowStart.value = next
  now.value = Date.now()
  if (storage !== null) await saveWindowStart(storage, next)
  if (platform !== null) await platform.stateChanged({ program: program.value, windowStartMillis: next }, напоминания())
  scheduleSync()
}

/**
 * Пересказать площадке текущее состояние окна.
 *
 * Подписка на уведомления живёт в браузере, а серверу её отдаёт запрос расписания —
 * отдельного маршрута нет. Значит, включив уведомления и не тронув окно, человек
 * оставался без них: сервер подписки не видел и пропускал задания. Теперь состояние
 * отправляется сразу — и при включении, и при запуске, если сервер о подписке не знает.
 */
export async function announceState(): Promise<void> {
  if (platform !== null) await platform.stateChanged({ program: program.value, windowStartMillis: windowStart.value }, напоминания())
}

export async function saveProfile(next: ChildProfile): Promise<void> {
  await rememberKid(next.name)
  profile.value = next
  if (storage !== null) {
    await saveProfileToStore(storage, next)
    const base = forProfile(next, Date.now())
    const applied = await applyCustomPhases(storage, base)
    customPhases.value = applied.phases === base.phases ? null : applied.phases
    isCustomProgram.value = await isCustomPhases(storage, base.id)
  }
  scheduleSync()
  await persistWindow(null) // новая программа — окно начинаем заново
}

export async function startWindow(): Promise<void> {
  const at = Date.now()
  justClosed.value = null
  await записатьСон(null)
  await persistWindow(at)
  if (storage !== null) {
    await logWindow(storage, at)
    windows.value = await loadWindows(storage)
    await recordPhase(at)
    scheduleSync()
  }
}

/**
 * Записывает, на какую фазу перешли. Момент перехода важнее плановой длительности:
 * по нему видно, что фазу оборвали раньше срока или, наоборот, затянули.
 */
/**
 * Начало текущего окна — то самое нажатие «Проснулся».
 *
 * Нельзя брать `windowStart`: в движке нет «текущей фазы», и переход к фазе — это сдвиг
 * начала окна назад. После первого же «Дальше» метка перестаёт совпадать с тем, что
 * записано в истории окон, и записи о конце окна оседали под ключом, которого в истории
 * нет, — то есть конец окна не находился никогда (найдено 23.09.2026).
 *
 * В историю начало пишется один раз, при запуске окна, и не меняется. Его и берём.
 *
 * Но только пока это окно открыто. Окно можно начать и не нажимая «Проснулся»: список фаз
 * нажимается всегда, и человек ставит таймер сразу на ту фазу, где он сейчас, — тогда
 * движок считает начало окна так, будто предыдущие фазы прошли по расписанию. Такое начало
 * в историю не попадало, и все записи нового бодрствования ложились под ключ предыдущего,
 * уже закрытого. В календаре два окна сливались в одно: «12:01–17:43» вместо «12:01–12:56»
 * и «16:32–17:43» (жалоба 09.10.2026).
 *
 * Поэтому правило такое: пока у последнего окна в истории нет конца, все записи его.
 * Есть конец, а таймер идёт — это новое бодрствование, и его начало заводится в истории.
 * Раньше конца предыдущего окна оно начаться не может: расчётное начало бывает и глубоко
 * в прошлом, а окна в истории идут по порядку и не наезжают друг на друга.
 */
async function началоОкна(): Promise<number | null> {
  const s = storage
  if (s === null) return windowStart.value
  const текущее = windowStart.value
  const записанные = await loadWindows(s)
  const последнее = записанные.at(-1)

  const завести = async (at: number): Promise<number> => {
    await logWindow(s, at)
    windows.value = await loadWindows(s)
    return at
  }

  // Истории ещё нет: первый таймер на устройстве включили сразу с нужной фазы
  if (последнее === undefined) return текущее === null ? null : завести(текущее)

  const конец = events.value
    .filter((e) => e.kind === 'window_end' && e.window === последнее)
    .sort((a, b) => a.at - b.at)[0]
  // Прошлое окно ещё открыто — все записи его
  if (конец === undefined) return последнее
  // Окно не идёт: писать некуда, кроме последнего (так приходит сброс и авто-закрытие)
  if (текущее === null) return последнее
  return завести(Math.max(текущее, конец.at + 1))
}
async function recordPhase(at: number): Promise<void> {
  const start = await началоОкна()
  if (storage === null || start === null) return
  const index = engine.currentIndex(state.value, at)
  const phase = program.value.phases[index]
  await запись(storage, {
    id: at,
    kind: 'phase',
    at,
    window: start,
    index,
    title: phase?.title ?? t('good_night'),
    planned: phase?.minutes ?? 0,
  })
}

/**
 * Человек вернулся к вкладке, которую браузер усыпил вчера вечером, и нажал раньше,
 * чем тикнули часы: нажатие легло бы в окно четырнадцатичасовой давности (случай 08.10.2026,
 * Samsung Internet). Брошенное окно закрываем первым делом — как сделал бы тик.
 */
async function закрытьБрошенное(at: number): Promise<boolean> {
  if (windowStart.value === null || !engine.isAbandoned(state.value, at)) return false
  await abandonWindow()
  return true
}

export async function nextPhase(): Promise<void> {
  const at = Date.now()
  if (await закрытьБрошенное(at)) return
  await persistWindow(engine.next(state.value, at).windowStartMillis)
  await recordPhase(at)
}

export async function goToPhase(index: number): Promise<void> {
  const at = Date.now()
  if (await закрытьБрошенное(at)) return
  await persistWindow(engine.goTo(state.value, index, at).windowStartMillis)
  await recordPhase(at)
}

export async function goToRitual(): Promise<void> {
  const at = Date.now()
  if (await закрытьБрошенное(at)) return
  await persistWindow(engine.toRitual(state.value, at).windowStartMillis)
  await recordPhase(at)
}

/**
 * Ответ на вопрос «Уснул?» — и конец окна.
 *
 * Окно бодрствования кончается в обоих случаях: ритуал позади. Вопрос только в том,
 * чем оно кончилось, — и это как раз то, ради чего события и заводились. Раньше конец
 * окна писал вообще только «Сброс»: аварийный путь записывался, задуманный нет.
 *
 * Закрыть окно может только этот ответ. По времени оно не закрывается: когда план
 * последней фазы выходит, она просто идёт дальше, а кнопка остаётся (правка 26.09.2026) —
 * иначе приложение решало за человека, что малыш уснул.
 */
export async function closeWindow(reason: 'asleep' | 'awake'): Promise<void> {
  const at = Date.now()
  const start = await началоОкна()
  if (storage !== null && start !== null) {
    await запись(storage, { id: at, kind: 'window_end', at, window: start, reason })
  }
  justClosed.value = { reason, at }
  // «Да, уснул» — с этого момента считаются напоминания о подъёме
  await записатьСон(reason === 'asleep' ? at : null)
  await persistWindow(null)
}

let сбрасываем = false

/**
 * Окно без ответа два часа после плана — закрываем сами, причина «без ответа».
 *
 * Это не «уснул за него»: это чтобы следующее «Проснулся» легло чисто и данные не
 * пропадали (решение 26.09.2026). Момент — расчётный, план плюс два часа, а не когда
 * открыли приложение: вкладка могла быть закрыта всё это время.
 */
export async function abandonWindow(): Promise<void> {
  if (сбрасываем) return
  сбрасываем = true
  try {
    const end = engine.windowEndMillis(state.value)
    if (end === null) return
    const at = end + engine.ABANDON_AFTER_SECONDS * 1000
    const start = await началоОкна()
    if (storage !== null && start !== null) {
      await запись(storage, { id: at, kind: 'window_end', at, window: start, reason: 'unanswered' })
    }
    justClosed.value = { reason: 'unanswered', at }
    await записатьСон(null)
    await persistWindow(null)
    // Конец окна — тоже запись для консультантов: не ждём следующего нажатия, чтобы отдать его
    scheduleSync()
  } finally {
    сбрасываем = false
  }
}

/**
 * «Проснулся» ночью — не окно, а ночное пробуждение: без фаз, будильников и пушей.
 * Записывается ради консультантов по сну: ночные пробуждения им важнее всего.
 */
export async function startNightWake(): Promise<void> {
  const at = Date.now()
  nightWake.value = at
  justClosed.value = null
  if (storage === null) return
  await storage.set(KEY.nightWakeStart, String(at))
  await запись(storage, { id: at, kind: 'night_wake', at, window: at })
  scheduleSync()
}

/**
 * Ночное пробуждение оказалось утренним: малыш не уснул обратно, день начался.
 *
 * Запись о ночном помечается удалённой, а окно начинается тем же моментом — временем,
 * когда нажали «Проснулся». Иначе утро пришлось бы начинать заново и терять полчаса.
 */
export async function nightWakeBecameMorning(): Promise<void> {
  const start = nightWake.value
  if (start === null || storage === null) return
  const запись = events.value.find((e) => e.kind === 'night_wake' && e.at === start && e.deleted !== true)
  if (запись !== undefined) await markEventDeleted(storage, запись)
  nightWake.value = null
  await storage.remove(KEY.nightWakeStart)
  await persistWindow(start)
  await logWindow(storage, start)
  windows.value = await loadWindows(storage)
  events.value = await loadEvents(storage)
  await recordPhase(Date.now())
  scheduleSync()
}

/** Конец ночного пробуждения: уснул — или наступило утро, а ответа так и не было. */
export async function endNightWake(reason: 'asleep' | 'unanswered', at = Date.now()): Promise<void> {
  const start = nightWake.value
  if (start === null) return
  nightWake.value = null
  if (storage === null) return
  await storage.remove(KEY.nightWakeStart)
  // Номер на единицу больше начала: при остановленных часах (прогоны) оба момента совпали бы
  await запись(storage, { id: at + 1, kind: 'night_wake_end', at, window: start, reason })
  scheduleSync()
}

// Часы идут — сверяем, не пора ли закрыть брошенное: окно без ответа или ночное
// пробуждение, дожившее до утра. Эффект, а не таймер: срабатывает и от тика,
// и от возврата вкладки, и сразу после подключения хранилища.
effect(() => {
  const сейчас = now.value
  if (storage === null) return
  if (windowStart.value !== null && engine.isAbandoned(state.value, сейчас)) void abandonWindow()
  const ночное = nightWake.value
  const утро = ночное === null ? null : nextDayStart(ночное, daySettings.value)
  if (ночное !== null && утро !== null && сейчас >= утро) void endNightWake('unanswered', утро)
})
export async function resetWindow(): Promise<void> {
  const at = Date.now()
  const start = await началоОкна()
  // Конец окна хранится записью, а не в списке начал: формат `windows_<месяц>`
  // читают часы и виджет Android, менять его нельзя.
  if (storage !== null && start !== null) {
    await запись(storage, { id: at, kind: 'window_end', at, window: start, reason: 'reset' })
  }
  await записатьСон(null)
  await persistWindow(null)
}

/** `null` — вернуть рекомендованные фазы. */
export async function setPhases(phases: Phase[] | null): Promise<void> {
  customPhases.value = phases
  isCustomProgram.value = phases !== null
  if (storage === null) return
  const id = baseProgram.value.id
  if (phases === null) await clearCustomPhases(storage, id)
  else await saveCustomPhases(storage, id, phases)
  scheduleSync()
}

export async function addNote(note: Note): Promise<void> {
  if (storage === null) return
  await addNoteToStore(storage, { ...note, updatedAt: Date.now() })
  notes.value = await loadNotes(storage)
  scheduleSync()
}

export async function updateNote(note: Note): Promise<void> {
  if (storage === null) return
  await updateNoteInStore(storage, { ...note, updatedAt: Date.now() })
  notes.value = await loadNotes(storage)
  scheduleSync()
}

export async function removeNote(note: Note): Promise<void> {
  if (storage === null) return
  await removeNoteFromStore(storage, note)
  notes.value = await loadNotes(storage)
  scheduleSync()
}
